import { ReactElement } from 'react';
import { CompanyUser } from '@/api';
import { useNavigate } from 'react-router';
import {
    UIButton,
    UISection,
    UISectionContent,
    UISectionHeader,
    UISectionState,
    UIText,
    useUIConfirmationModal
} from '@/ui';
import { useCompanyContext } from '../providers';
import { useFetchUserCounts } from '../queries';
import { useDeleteCompanyUserAction, useResendInviteAction } from '../actions';
import { useCompaniesTranslation } from '../hooks';

type ActionsSettingsSectionProps = {
    companyUser: CompanyUser;
};

/**
 * Resending an invite and removing a user from the company. Both buttons are
 * always shown — each action carries its own reason, so a viewer who may not
 * use one is told why rather than being shown a section with nothing in it.
 */
export function MemberActionsSettingsSection(
    props: ActionsSettingsSectionProps
): ReactElement {
    const { companyUser } = props;

    const { t } = useCompaniesTranslation();
    const navigate = useNavigate();

    const { company } = useCompanyContext();
    // Removing the last owner is blocked, so the section waits for the count
    // rather than deciding without it.
    const {
        counts,
        status: countsStatus,
        refetch
    } = useFetchUserCounts(company.id);
    const confirmationModal = useUIConfirmationModal();

    const resendInviteAction = useResendInviteAction({ user: companyUser });
    const deleteUserAction = useDeleteCompanyUserAction({
        user: companyUser,
        ownerCount: counts?.joinedOwner ?? 0
    });

    if (!counts) {
        return (
            <UISectionState
                title={t('components.member_actions_settings_section.title')}
                status={countsStatus}
                errorText={t(
                    'components.member_actions_settings_section.load_error'
                )}
                onRetry={refetch}
            />
        );
    }

    function onRemove() {
        confirmationModal.open({
            title: t(
                'components.member_actions_settings_section.remove_confirm_title'
            ),
            confirmText: t(
                'components.member_actions_settings_section.remove_confirm_button'
            ),
            confirmVariant: 'danger',
            cancelText: t('shared.labels.cancel'),
            onConfirm: () => {
                confirmationModal.setLoading(true);
                deleteUserAction
                    .deleteUser()
                    .then(() => {
                        // The user this page is about no longer exists, so
                        // fall back to the list rather than refetching a 404.
                        void navigate(
                            `/company/${companyUser.companyId}/settings/members`
                        );
                    })
                    .catch(() => {}) // Error handled in hook
                    .finally(() => {
                        confirmationModal.setLoading(false);
                        confirmationModal.close();
                    });
            },
            onCancel: () => confirmationModal.close()
        });
    }

    return (
        <UISection>
            <UISectionHeader
                title={t('components.member_actions_settings_section.title')}
            />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    {/* Only meaningful while the invite is outstanding. */}
                    {!companyUser.joined && (
                        <Row
                            label={t('shared.labels.resend_invite')}
                            description={t(
                                'components.member_actions_settings_section.resend_description'
                            )}
                        >
                            <UIButton
                                text={t('shared.labels.resend_invite')}
                                leftIcon="send"
                                loading={resendInviteAction.loading}
                                disabled={!resendInviteAction.isAllowed}
                                disabledTooltip={resendInviteAction.reason}
                                onClick={() => {
                                    resendInviteAction
                                        .resendInvite()
                                        .catch(() => {}); // Error handled in hook
                                }}
                            />
                        </Row>
                    )}

                    <Row
                        label={t(
                            'components.member_actions_settings_section.remove_label'
                        )}
                        description={t(
                            'components.member_actions_settings_section.remove_description'
                        )}
                    >
                        <UIButton
                            text={t('shared.labels.remove')}
                            variant="textDanger"
                            leftIcon="minus-circle"
                            loading={deleteUserAction.loading}
                            disabled={!deleteUserAction.isAllowed}
                            disabledTooltip={deleteUserAction.reason}
                            onClick={onRemove}
                        />
                    </Row>
                </div>
            </UISectionContent>
        </UISection>
    );
}

function Row(props: {
    label: string;
    description: string;
    children: ReactElement;
}): ReactElement {
    const { label, description, children } = props;
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 16
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    gap: 4
                }}
            >
                <UIText>{label}</UIText>
                <UIText type="default2">{description}</UIText>
            </div>
            <div style={{ display: 'flex', flex: 1 }}>{children}</div>
        </div>
    );
}
